import React from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import { FraudSignal, FraudStatus } from "../../services/fraudService";
import { FRAUD_STATUS_LABELS, fraudLikelihood, fraudPctColor, fraudSignalLabel } from "../../utils/fraud";

const STATUS_STYLE: Record<FraudStatus, { color: "error" | "default"; sx?: object }> = {
    OPEN: { color: "error" },
    CONFIRMED: { color: "error", sx: { bgcolor: "#7f1d1d", color: "#fff" } },
    CLEARED: { color: "default" },
};

/** "Potential fraud" (red) / "Fraud confirmed" (dark red) / "Cleared" (grey); unknown statuses render nothing. */
export const FraudStatusChip: React.FC<{ status: string; tooltip?: string }> = ({ status, tooltip }) => {
    const style = STATUS_STYLE[status as FraudStatus];
    if (!style) return null;
    const chip = (
        <Chip
            size="small"
            data-testid="fraud-status-chip"
            label={FRAUD_STATUS_LABELS[status as FraudStatus]}
            color={style.color}
            sx={{ fontWeight: 600, ...style.sx }}
        />
    );
    return tooltip ? <Tooltip title={tooltip}><span>{chip}</span></Tooltip> : chip;
};

/** "93% · High", red from 80%, amber from 50%. */
export const FraudPctChip: React.FC<{ fraudPct: number; certainty: string }> = ({ fraudPct, certainty }) => (
    <Chip
        size="small"
        data-testid="fraud-pct-chip"
        label={fraudLikelihood(fraudPct, certainty)}
        color={fraudPctColor(fraudPct)}
        sx={{ fontWeight: 700 }}
    />
);

/** One chip per signal; hovering shows the server's detail. */
export const FraudSignalChips: React.FC<{ signals: FraudSignal[] }> = ({ signals }) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {signals.map((s, i) => (
            <Tooltip key={`${s.code}-${i}`} title={`${s.detail} (severity ${s.severity})`}>
                <Chip
                    size="small"
                    variant="outlined"
                    color={s.severity >= 3 ? "error" : "warning"}
                    label={fraudSignalLabel(s.code)}
                />
            </Tooltip>
        ))}
    </Box>
);
